import type { ValidationIssue } from '../../../errors'
import type { ZodIssue } from '../types'
import { accepts, coerceDeep, coerceLeaf, isContainer } from './coerce'
import { passesDateThrough } from './dates'
import type { FieldPlan } from './field-plan'
import { type AnyZod, inspect, shapeEntries } from './introspect'
import { invalidTypeMessage, requiredMessage, toFieldIssue } from './messages'

// The pipeline, per field:
//
//   1. normalize absence  →  2. coerce to the declared type  →  3. validate (the author's own schema)
//
// The order is load-bearing. Absence first, or a blank becomes garbage (`Number('')` is 0). Validation last,
// and against the author's schema unchanged, so `.min()`, `.max()`, `.int()`, `.regex()`, enum membership and
// refinements all work natively — nothing here reimplements them.

export interface FieldResult {
  // Absent from the output entirely when the field resolved to "not present" and had nothing to stand in.
  value?: unknown
  present: boolean
  issues: ValidationIssue[]
}

const resolved = (value: unknown): FieldResult => ({ value, present: true, issues: [] })
const rejected = (issues: ValidationIssue[]): FieldResult => ({ present: false, issues })

// `undefined` and `null` are always absence. `''` is absence only where the type has no empty value, or
// where a declared default can absorb it — on a string it is otherwise a real value.
function isAbsent(value: unknown, plan: FieldPlan): boolean {
  if (value === undefined || value === null) {
    return true
  }

  if (value !== '') {
    return false
  }

  // Same rule the deep walk applies (see ./coerce.ts `isBlank`): `''` is a value only on a plain string
  // type. A union resolves to its first branch for publishing, but that must not turn a blank into a value.
  if (plan.isUnion) {
    return true
  }

  return plan.coreType !== 'string' || plan.absorbsBlank
}

// Absence becomes whichever form the schema accepts, so the author's choice of modifier decides what the
// handler sees rather than what the client happened to send.
function normalizeAbsence(value: unknown, plan: FieldPlan): FieldResult {
  if (plan.absence === 'required') {
    return rejected([{ path: plan.name, code: 'invalid_type', message: requiredMessage(plan) }])
  }

  if (plan.absence === 'as-received') {
    // `.nullish()` accepts both, so a blank collapses to `undefined` and the rest arrive as sent.
    return validate(value === '' ? undefined : value, plan)
  }

  return validate(plan.absence === 'null' ? null : undefined, plan)
}

// Step 3. Zod short-circuits on the absent form it accepts, so a constraint never fires on an absent value.
function validate(value: unknown, plan: FieldPlan): FieldResult {
  const schema = plan.schema as unknown as {
    safeParse: (v: unknown) => { success: boolean; data?: unknown; error?: { issues?: ZodIssue[] } }
  }

  let result

  try {
    result = schema.safeParse(value)
  } catch {
    // An async refinement cannot be evaluated here; surface it as a plain failure rather than crashing.
    return rejected([{ path: plan.name, code: 'custom', message: `«${ plan.label }» could not be validated` }])
  }

  return result.success
    ? resolved(result.data)
    : rejected((result.error?.issues ?? []).map(issue => toFieldIssue(plan, issue, value)))
}

export function normalizeField(value: unknown, plan: FieldPlan): FieldResult {
  if (isAbsent(value, plan)) {
    return normalizeAbsence(value, plan)
  }

  // A container is WALKED — its declared properties and its elements are coerced in place — where a scalar
  // is converted outright. A container that cannot be walked (a string where a list was declared) is passed
  // through for zod to reject as the wrong type.
  if (isContainer(plan.coreType)) {
    return restore(validate(coerceDeep(value, plan.schema), plan), value, plan)
  }

  const coerced = coerceLeaf(value, inspect(plan.schema))

  if (!coerced.ok) {
    return rejected([{
      path   : plan.name,
      code   : 'invalid_type',
      message: invalidTypeMessage(plan, coerced.expected as string),
    }])
  }

  return restore(validate(coerced.value, plan), value, plan)
}

/**
 * Give a `z.date()` back the value the host actually sent.
 *
 * Validation needs a real `Date` — `z.date()` accepts nothing else, and `.min()` / `.max()` compare instants
 * — but converting is not what the handler should see: `'2024-06-01'` becomes `2024-06-01T00:00:00.000Z`,
 * inventing a time of day and a timezone nobody chose. So the check runs on a converted copy and the
 * original is restored afterwards, at every depth.
 *
 * `z.coerce.date()` is untouched: the author asked for a `Date`, so zod's output stands.
 */
function restore(result: FieldResult, original: unknown, plan: FieldPlan): FieldResult {
  if (!plan.restoresDates || !result.present || result.issues.length) {
    return result
  }

  if (plan.passThroughDate) {
    return resolved(original)
  }

  return resolved(restoreDeep(result.value, original, plan.schema))
}

// Walk zod's output and the submitted value together, taking the ORIGINAL wherever the declaration is a
// pass-through date. Only declared shapes are walked — the same rule the coercion walk follows.
function restoreDeep(output: unknown, original: unknown, schema: AnyZod | undefined): unknown {
  if (!schema || output == null || original == null) {
    return output
  }

  const ins = inspect(schema)

  if (passesDateThrough(ins)) {
    return original
  }

  // A union was coerced through whichever branch accepted the value, so the restore walk has to follow that
  // same branch — the resolved first one says nothing about which date, if any, the value actually took.
  if (ins.branches) {
    const branch = ins.branches.find(option => accepts(option, output))

    return branch ? restoreDeep(output, original, branch) : output
  }

  const element = ins.core?._zod?.def?.element as AnyZod | undefined

  if (ins.coreType === 'array' && element && Array.isArray(output) && Array.isArray(original)) {
    return output.map((item, index) => restoreDeep(item, original[index], element))
  }

  const valueType = ins.core?._zod?.def?.valueType as AnyZod | undefined

  if (ins.coreType === 'record' && valueType && isPlain(output) && isPlain(original)) {
    return Object.fromEntries(
      Object.entries(output).map(([key, item]) => [key, restoreDeep(item, original[key], valueType)])
    )
  }

  if (ins.coreType === 'object' && isPlain(output) && isPlain(original)) {
    const restored: Record<string, unknown> = { ...output }

    for (const [name, property] of shapeEntries(ins.core)) {
      if (name in output) {
        restored[name] = restoreDeep(output[name], original[name], property)
      }
    }

    return restored
  }

  return output
}

const isPlain = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)
